/**
 * DRPX Courier — Notification-Utilities
 *
 * Browser-Benachrichtigungen, Permission-Requests, Tab-Titel-Blink.
 *
 * @module components/pwa/notification-utils
 */

import { useRef, useEffect } from 'react';
import type { TranslateFn, ToastType } from '../../types';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

type ToastFn = (msg: string, type?: ToastType) => void;

// ═══════════════════════════════════════════════════════════════
// Funktionen
// ═══════════════════════════════════════════════════════════════

/**
 * requestNotificationPermission — Fragt Browser-Benachrichtigungs-Erlaubnis ab.
 */
export function requestNotificationPermission(t: TranslateFn, openToast: ToastFn): void {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    openToast(t('notifPermGranted'), 'success');
    return;
  }
  if (Notification.permission === 'denied') {
    openToast(t('notifPermDenied'), 'warning');
    return;
  }
  void Notification.requestPermission().then((perm) => {
    if (perm === 'granted') openToast(t('notifPermGranted'), 'success');
    else openToast(t('notifPermDenied'), 'warning');
  });
}

/**
 * sendBrowserNotif — Sendet eine native Browser-Benachrichtigung.
 */
export function sendBrowserNotif(title: string, body: string, onClick?: () => void): void {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body,
      icon: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='14' fill='%2316a34a'/%3E%3Ctext x='9' y='40' fill='%23fff' font-family='sans-serif' font-size='23' font-weight='800'%3ED%3C/text%3E%3Ctext x='28' y='40' fill='%23f59e0b' font-family='sans-serif' font-size='23' font-weight='800'%3EX%3C/text%3E%3Cpath d='M46 10Q52 10 52 16Q52 21 46 26Q40 21 40 16Q40 10 46 10Z' fill='%234ade80'/%3E%3Ccircle cx='46' cy='16' r='3.3' fill='%23fff'/%3E%3C/svg%3E",
      badge:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='14' fill='%2316a34a'/%3E%3Ctext x='9' y='40' fill='%23fff' font-family='sans-serif' font-size='23' font-weight='800'%3ED%3C/text%3E%3Ctext x='28' y='40' fill='%23f59e0b' font-family='sans-serif' font-size='23' font-weight='800'%3EX%3C/text%3E%3Cpath d='M46 10Q52 10 52 16Q52 21 46 26Q40 21 40 16Q40 10 46 10Z' fill='%234ade80'/%3E%3Ccircle cx='46' cy='16' r='3.3' fill='%23fff'/%3E%3C/svg%3E",
      tag: 'drpx-' + Date.now(),
      requireInteraction: false,
    });
    if (onClick) {
      n.onclick = () => {
        window.focus();
        onClick();
        n.close();
      };
    }
    setTimeout(() => {
      n.close();
    }, 8000);
  } catch {
    /* ignored */
  }
}

/**
 * useTabTitleBlink — Hook: Blinkt den Browser-Tab-Titel bei ungelesenen Nachrichten.
 */
export function useTabTitleBlink(unreadCount: number): void {
  const origTitle = useRef(document.title);
  useEffect(
    () => {
      if (!origTitle.current) origTitle.current = document.title;
      if (unreadCount <= 0) {
        document.title = origTitle.current;
        return;
      }
      let on = true;
      const iv = setInterval(() => {
        document.title = on ? '(' + unreadCount + ') Neue Nachricht!' : origTitle.current;
        on = !on;
      }, 1200);
      return () => {
        clearInterval(iv);
        document.title = origTitle.current;
      };
    },
    [unreadCount],
  );
}
